import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import type { Adapter } from '@tronweb3/tronwallet-abstract-adapter';
import { WalletReadyState } from '@tronweb3/tronwallet-abstract-adapter';
import { TronWeb } from 'tronweb';

import type {
  TronAdapter,
  TronWalletProvider,
} from '../../../TronWalletProvider.types';
import { convertChainIdToDecimal } from '../../../utils/convertChainIdToDecimal';
import { tronExecuteCheckoutTransaction } from '../../../utils/tronExecuteCheckoutTransaction';
import { tronTransferAmount } from '../../../utils/tronTransferAmount';

type CreateWalletProviderForTronWalletAdapterParams = {
  adapter: Adapter;
  dynamicClient: DynamicClient;
};

export const createWalletProviderForTronWalletAdapter = ({
  dynamicClient,
  adapter,
}: CreateWalletProviderForTronWalletAdapterParams):
  | TronWalletProvider
  | undefined => {
  const chain: Chain = 'TRON';

  // we have to do this type cast because these adapters have the _wallet property private,
  // but we need to access it to get the TronWeb instance
  const tronAdapter = adapter as unknown as TronAdapter;

  // Wallet is not installed
  if (tronAdapter.readyState !== WalletReadyState.Found) {
    return undefined;
  }

  const metadata: WalletProviderMetadata = {
    displayName: tronAdapter.name,
    icon: tronAdapter.icon,
  };

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: metadata.displayName,
    walletProviderType,
  });

  const connect: TronWalletProvider['connect'] = async () => {
    await tronAdapter.connect();

    // Some wallets (Trust, Bitget) populate the address asynchronously after connect()
    // Wait briefly for the address to be populated by the wallet
    const startTime = Date.now();
    while (!tronAdapter.address?.length && Date.now() - startTime < 1000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return {
      addresses: tronAdapter.address ? [{ address: tronAdapter.address }] : [],
    };
  };

  const disconnect: TronWalletProvider['disconnect'] = async () => {
    return tronAdapter.disconnect();
  };

  const getActiveNetworkId: TronWalletProvider['getActiveNetworkId'] =
    async () => {
      const networkInfo = await tronAdapter.network();

      const networkId = convertChainIdToDecimal(networkInfo.chainId);

      return { networkId };
    };

  const getConnectedAddresses: TronWalletProvider['getConnectedAddresses'] =
    async () => {
      return {
        addresses: tronAdapter.address ? [tronAdapter.address] : [],
      };
    };

  const signMessage: TronWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedMessage = await tronAdapter.signMessage(message);

    return {
      signature: signedMessage,
    };
  };

  const getTronWeb: TronWalletProvider['getTronWeb'] = async ({
    walletAccount,
  }) => {
    const tronWeb =
      tronAdapter._wallet?.tronWeb || tronAdapter._wallet?.tron?.tronWeb;

    if (tronWeb) {
      return tronWeb;
    }

    const activeNetworkData = await getActiveNetworkData(
      { walletAccount },
      dynamicClient
    );
    assertDefined(activeNetworkData, 'No active network data found');

    const url = activeNetworkData.networkData?.rpcUrls.http[0];
    assertDefined(url, 'No RPC URL found for active network');

    return new TronWeb({ fullHost: url });
  };

  return {
    chain,
    connect,
    disconnect,
    executeCheckoutTransaction: (args) =>
      tronExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    getTronWeb,
    groupKey: formatWalletProviderGroupKey(metadata.displayName),
    key,
    metadata,
    signMessage,
    transferAmount: (args) => tronTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
